import { useEffect, useState } from "react";
import { Upload, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { assetUrl, formatApiError } from "@/lib/api";
import { TID } from "@/constants/tids";

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/media").then((r) => setItems(r.data));

  useEffect(() => {
    load();
  }, []);

  const onUpload = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/media/upload", fd);
      }
      toast.success(`${files.length} fișier(e) încărcate`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ștergi acest fișier?")) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success("Fișier șters");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copy = (url) => {
    navigator.clipboard.writeText(assetUrl(url));
    toast.success("URL copiat");
  };

  return (
    <div>
      <div className="mb-6 sm:mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Media</div>
        <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0A0A0A]">Bibliotecă imagini</h1>
        <p className="mt-1 text-sm text-gray-500">{items.length} fișiere</p>
      </div>

      <label
        htmlFor="mediaupload"
        className={`flex flex-col items-center justify-center gap-2 h-32 sm:h-40 rounded-2xl border-2 border-dashed border-gray-200 hover:border-[#D4AF37] active:border-[#D4AF37] bg-white cursor-pointer transition-colors ${
          uploading ? "opacity-60" : ""
        }`}
      >
        <Upload className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
        <div className="text-center px-4">
          <div className="text-sm font-bold text-[#0A0A0A]">
            {uploading ? "Se încarcă..." : "Apasă pentru a încărca imagini"}
          </div>
          <div className="text-xs text-gray-500 mt-1">JPG, PNG, WEBP</div>
        </div>
        <input
          id="mediaupload"
          data-testid={TID.adminMediaUpload}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          disabled={uploading}
          onChange={(e) => {
            if (e.target.files?.length) onUpload([...e.target.files]);
            e.target.value = "";
          }}
        />
      </label>

      <div className="mt-4 sm:mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
        {items.map((m) => (
          <div key={m.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="relative aspect-square bg-gray-100">
              <img src={assetUrl(m.url)} alt={m.filename} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute top-2 right-2 flex flex-col gap-1.5">
                <button
                  onClick={() => copy(m.url)}
                  className="h-9 w-9 rounded-full bg-white/95 backdrop-blur text-[#0A0A0A] flex items-center justify-center shadow-md active:scale-95"
                  title="Copiază URL"
                  aria-label="Copiază URL"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <button
                  onClick={() => remove(m.id)}
                  className="h-9 w-9 rounded-full bg-red-600 text-white flex items-center justify-center shadow-md active:scale-95"
                  title="Șterge"
                  aria-label="Șterge"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-2.5 sm:p-3">
              <div className="text-xs font-semibold text-[#0A0A0A] truncate">{m.filename}</div>
              <div className="text-[10px] text-gray-400">{Math.round(m.size / 1024)} KB</div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center text-sm text-gray-400 py-12">
            Bibliotecă goală. Începe prin a încărca imagini.
          </div>
        )}
      </div>
    </div>
  );
}
